'use client'

import PropTypes from 'prop-types'
import { useEffect, useState } from 'react'
import { ReactSelectMenu } from '@repo/ui/ReactSelectMenu'
import { useMattersStore } from '@repo/store/useMattersStore'
import { MatterSearchResultCard } from '@repo/ui/MatterSearchResultCard'
import { useDebounce } from '@uidotdev/usehooks'

export const MatterSelect = ({
  testData = null,
  value = null,
  onChange = (val) => {
    console.log(val)
  },
  sx,
  includeBlankMatterOption = false,
  disableArchived = false,
  disableGlobal = false,
  disableRestricted = false,
  disabled = false,
  required,
  label = 'Matter',
  size = 'small',
  name = 'matter',
  errors = {},
  menuZindex,
  menuWidth = null
}) => {
  const { fetchMatterSearch, matterSearchResults, recentMatters, currentClientMatterNo, clearMatterSearchResults } =
    useMattersStore()
  const [searchCriteria, setSearchCriteria] = useState('')
  const debouncedSearchCriteria = useDebounce(searchCriteria, 300)
  const [isLoading, setIsLoading] = useState(false)
  const [options, setOptions] = useState(testData || recentMatters || [])

  useEffect(() => {
    if (testData) return

    const fetchSearchResults = async () => {
      setIsLoading(true)
      clearMatterSearchResults()
      const { searchQueriesStillInProgress } = await fetchMatterSearch({
        searchCriteria: debouncedSearchCriteria,
        pageSize: 50
      })
      setIsLoading(!searchQueriesStillInProgress)
    }

    if (debouncedSearchCriteria) {
      fetchSearchResults()
    }
  }, [testData, fetchMatterSearch, debouncedSearchCriteria, clearMatterSearchResults])

  useEffect(() => {
    if (testData) return

    let options = debouncedSearchCriteria ? matterSearchResults : recentMatters

    options = options.map((opt) => {
      if (disableArchived && opt.dateArchived) opt.isDisabled = true
      if (disableGlobal && opt.workflowTypeId === 'GLOBAL') opt.isDisabled = true
      if (disableGlobal && opt.workflowTypeId === 'GLOBAL') opt.isDisabled = true
      if (disableRestricted && opt.isUserRestricted) opt.isDisabled = true
      return opt
    })

    if (includeBlankMatterOption && !debouncedSearchCriteria) {
      options.unshift({ clientMatterNo: '-0' })
    }

    setOptions(options || [])
  }, [
    testData,
    debouncedSearchCriteria,
    matterSearchResults,
    recentMatters,
    disableArchived,
    disableGlobal,
    disableRestricted,
    includeBlankMatterOption
  ])

  return (
    <div data-testid="MatterSelect" style={{ width: sx?.width || '100%' }}>
      <ReactSelectMenu
        label={label ?? ''}
        name={name}
        disabled={disabled}
        onChange={onChange}
        items={options}
        value={value || null}
        errors={errors}
        sx={sx}
        searchable
        size={size}
        menuZindex={menuZindex}
        menuWidth={menuWidth}
        valueKey="clientMatterNo"
        labelKey="searchTerm"
        required={required}
        loading={isLoading}
        onSearchTermChange={setSearchCriteria}
        valueCustomRenderFunction={(matter) => {
          return (
            <MatterSearchResultCard
              matter={matter}
              currentClientMatterNo={currentClientMatterNo}
              disableArchived={disableArchived}
              disableGlobal={disableGlobal}
              disableRestricted={disableRestricted}
              isListOption={false}
            />
          )
        }}
        optionCustomRenderFunction={(matter) => {
          return (
            <MatterSearchResultCard
              matter={matter}
              currentClientMatterNo={currentClientMatterNo}
              disableArchived={disableArchived}
              disableGlobal={disableGlobal}
              disableRestricted={disableRestricted}
            />
          )
        }}
      />
    </div>
  )
}

MatterSelect.propTypes = {
  testData: PropTypes.array,
  value: PropTypes.object,
  onChange: PropTypes.func,
  sx: PropTypes.object,
  includeBlankMatterOption: PropTypes.bool,
  disableArchived: PropTypes.bool,
  disableGlobal: PropTypes.bool,
  disableRestricted: PropTypes.bool,
  disabled: PropTypes.bool,
  required: PropTypes.bool,
  label: PropTypes.string,
  size: PropTypes.string,
  name: PropTypes.string,
  errors: PropTypes.object,
  clientFilter: PropTypes.string,
  menuZindex: PropTypes.number,
  menuWidth: PropTypes.string
}
